/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */
import { IPoolLike } from './IPoolLike';
import { Sprite, Container, DisplayObject } from 'pixi.js-legacy';
import { FancySprite } from '@appCanvasCommon/graphics/FancySprite';
import { FancyContainer } from '@appCanvasCommon/graphics/FancyContainer';
import { FancyText } from '@appCanvasCommon/fontpath/FancyText';
import { HighResolutionText } from '@appCanvasCommon/text/HighResolutionText';
import { IDestroyOptions } from '@appCanvasCommon/core/DestroyOptions';

/**
 * Simple interface to mark an object as something that requires special functionality when the pool is reset
 */
export interface IPoolReset {
	poolReset: () => void;
}
/**
 * Generic object pool implementation that manages reusable objects to avoid frequent allocation/deallocation.
 * This helps reduce garbage collection pressure and improves performance in scenarios where objects
 * are frequently created and destroyed.
 */
export class GenericPool<T extends DisplayObject> implements IPoolLike<T> {
	/**
	 * Array storing the pooled objects
	 */
	protected pool: T[] = [];
	/**
	 * Current index in the pool indicating the next available slot
	 */
	protected position: number = 0;
	/**
	 * Creates a new GenericPool instance
	 * @param creationFn Function that creates new instances of type T when the pool needs to expand
	 * @param _container The container to add the child objects to
	 */
	constructor(protected creationFn: () => T, protected _container: Container) {}
	/**
	 * Retrieves an object from the pool. If no objects are available at the current position,
	 * creates a new one using the creation function and adds it to the pool.
	 * @returns An object of type T from the pool
	 */
	public grab(): T {
		let item;
		if (this.position < this.pool.length) {
			item = this.pool[this.position];
			if (item.attach) {
				item.attach();
			}
		} else {
			item = this.creationFn();
			this.pool.push(item);
			this._container.addChild(item);
		}
		this.position++;

		item.visible = true;
		item.renderable = true;
		return item;
	}
	/**
	 * Resets the pool position back to 0, making all pooled objects available for reuse.
	 * Also resets any pooled objects that have a 'defaultState' property to their default values.
	 */
	public reset() {
		this.position = 0;
		// If a 'defaultState' object exists, reset the pooled object properties to the default
		this.pool.forEach((item: any) => {
			if (item.defaultState) {
				for (const key in item.defaultState) {
					item[key] = item.defaultState[key];
				}
			}
			if (item.poolReset) {
				item.poolReset();
			}
		});
		this.hide();
	}
	/**
	 * Destroys the pool. Base implementation is empty - subclasses should override
	 * to perform proper cleanup of pooled objects.
	 */
	public destroy(options?: IDestroyOptions): void {
		if (!this.pool) {
			return;
		}
		this.pool.forEach((thing: T) => {
			// Hack: Pixi internals does not have a mechanism to prevent a thing from being destroyed multiple times; which causes the app to boom.
			// Pixi does however have a _destroyed flag, which is a protected accessor.
			// So what we are doing here is casting the generic type as any so we can access the protected _destroyed flag
			// and preventing cached objects from being destroyed if they were previously destroyed.
			// The initial incident that caused this to be added was loading the Milestone and Constraint textures from the global
			// texture cache as part of a GenericPool<Sprite>. When the app is destroyed, the objects for the global texture cache
			// are destroyed correctly. Later in the destroy flow, the GenericPool<Sprite> accessing that cache is destroyed, which
			// then destroyed the source objects related to it, however in this case, those objects were already destroyed earlier in cleanup.
			if (!(<any>thing)._destroyed) {
				thing.destroy(options);
			}
		});
		this.pool = null;
	}
	/**
	 * Shows all elements in the pool.
	 */
	public show(): void {
		this.pool.forEach((thing: T) => (thing.visible = true));
	}
	/**
	 * Hides all elements in the pool.
	 */
	public hide(): void {
		this.pool.forEach((thing: T) => (thing.visible = false));
	}
}
/**
 * Object pool specifically designed for PIXI.js Sprite objects.
 * Manages sprite visibility and automatically adds new sprites to a container.
 */
export class SpritePool extends GenericPool<Sprite> {
	/**
	 * Creates a new SpritePool
	 * @param creationFn Function that creates new Sprite instances
	 * @param container PIXI Container where sprites will be added
	 */
	constructor(protected creationFn: () => Sprite, container: Container) {
		super(creationFn, container);
	}
	/**
	 * Hides all sprites in the pool by setting renderable to false
	 */
	public hide(): void {
		this.pool.forEach((s: Sprite): boolean => (s.renderable = false));
	}
	/**
	 * Shows all sprites in the pool by setting renderable to true
	 */
	public show(): void {
		this.pool.forEach((s: Sprite): boolean => (s.renderable = true));
	}
}
/**
 * Object pool specifically designed for FancySprite objects.
 * Manages sprite visibility and automatically adds new sprites to a container.
 */
export class FancySpritePool extends GenericPool<FancySprite> {
	/**
	 * Creates a new FancySpritePool
	 * @param creationFn Function that creates new FancySprite instances
	 * @param _container FancyContainer where sprites will be managed
	 * @param onlyPool If true, detaches ALL children on reset; if false, only detaches pooled children
	 */
	constructor(
		protected creationFn: () => FancySprite,
		protected _container: FancyContainer,
		protected onlyPool: boolean = false
	) {
		super(creationFn, _container);
	}
	/**
	 * Resets the pool and hides all sprites by setting their visible property to false
	 */
	public reset(): void {
		super.reset();
		if (this.onlyPool) {
			this._container.detachAllChildren();
		} else {
			this._container.detachChildren(this.pool);
		}
	}
	/**
	 * Destroys all sprites in the pool, properly cleaning up PIXI resources
	 * @param options Optional destruction options passed to each sprite's destroy method
	 */
	public destroy(options?: IDestroyOptions): void {
		this.pool.forEach((s: FancySprite) => s.destroy(options));
	}
	/**
	 * Hides all sprites in the pool by detaching all of the objects from the scene graph
	 */
	public hide(): void {
		this._container.detachChildren(this.pool);
	}
	/**
	 * Shows all sprites in the pool by attaching all of the pooled objects back to the scene graph
	 */
	public show(): void {
		this._container.attachChildren(this.pool);
	}
}
/**
 * Object pool for text objects (FancyText or HighResolutionText).
 * Manages text object visibility and container membership.
 */
export class TextPool extends GenericPool<FancyText | HighResolutionText> {
	/**
	 * Creates a new TextPool
	 * @param creationFn Function that creates new text instances
	 * @param container PIXI Container where text objects will be added
	 */
	constructor(protected creationFn: () => FancyText | HighResolutionText, protected container: Container) {
		super(creationFn, container);
	}
	/**
	 * Hides all text objects by setting renderable to false
	 */
	public hide(): void {
		this.pool.forEach((s: FancyText | HighResolutionText): boolean => (s.renderable = false));
	}
	/**
	 * Shows all text objects by setting renderable to true
	 */
	public show(): void {
		this.pool.forEach((s: FancyText | HighResolutionText): boolean => (s.renderable = true));
	}
}
